import { Info } from '@velocity-exchange/icons';
import { Slottable } from '@radix-ui/react-slot';
import { ConditionalAsChild } from 'src/components/primitives/ConditionalAsChild';
import Tooltip, {
	TooltipProps,
} from 'src/components/primitives/Tooltip/Tooltip';
import {
	DEFINITION_TOOLTIP_CONFIG_BY_ID,
	DefinitionTooltipID,
} from 'src/components/Tooltip/DefinitionTooltip/definitionTooltipConfig';
import { cm } from 'src/utils/classNames';

interface Props extends Omit<TooltipProps, 'content'> {
	/**
	 * This is optional because there are cases where we want to conditionally
	 * render a tooltip based on some other value, and this makes the DX better.
	 * If not passed, it will simply render `children` directly.
	 */
	definitionId?: DefinitionTooltipID;
	/**
	 * Renders an `Info` icon next to the trigger content.
	 * Uses `autoSize` / `currentColor` for its size / color.
	 */
	withIcon?: boolean;
}

export default function DefinitionTooltip({
	definitionId,
	className,
	asChild,
	children,
	withIcon,
	...rest
}: Props) {
	if (!definitionId) {
		return (
			<ConditionalAsChild
				className={className}
				asChild={asChild}
				fallback="span"
			>
				{children}
			</ConditionalAsChild>
		);
	}

	const { title, body } = DEFINITION_TOOLTIP_CONFIG_BY_ID[definitionId];

	const tooltipContent = (
		<div className="flex flex-col gap-y-sm">
			<span className="typo-label-xs">{title}</span>
			<span
				// `body` font size inherited internally from `Tooltip`.
				className="flex flex-col gap-y-xs"
			>
				{body}
			</span>
		</div>
	);

	return (
		<Tooltip
			className={cm(withIcon && 'flex items-center gap-x-xs', className)}
			asChild={asChild}
			content={tooltipContent}
			{...rest}
		>
			<Slottable>{children}</Slottable>
			{withIcon && <Info autoSize />}
		</Tooltip>
	);
}
